/**
 * Dark-mode values for every leaf of `rawColors` (same deep shape, same key paths).
 * Consumed by `colorVars/installColorVars.ts`, which emits them under
 * `[data-mb-theme="dark"]`. Violet brand: page #171126, surfaces step lighter.
 */
import type { rawColors } from './colors';

export type DeepStringRecord<T> = {
  [K in keyof T]: T[K] extends string ? string : DeepStringRecord<T[K]>;
};

export const darkRawColors: DeepStringRecord<typeof rawColors> = {
  brand: {
    primary: "#6442B3",
    primaryLight: "#AA97DB",
    primaryDark: "#4B3286",
    primaryDarker: "#18102B",
  },

  grey: {
    50: "#1D1730",
    100: "#241D38",
    200: "#2E2642",
    300: "#2A2340",
    400: "#3A3152",
    500: "#6B6580",
    600: "#9B95AD",
    700: "#C4BFD2",
    800: "#C4BFD2",
    900: "#F3F1F7",
  },

  structural: {
    white: "#1F1830",
    ink: "#241C35",
    themeLight: "#171126",
    themeLight75: "rgba(42, 34, 64, 0.75)",
    navItem: "#2A2240",
    50: "#1A1428",
    100: "#2A2240",
    200: "#352C4D",
  },

  primary: {
    50: "#241B3A",
    100: "#2D2148",
    200: "#3A2B5C",
    300: "#9780CC",
    400: "#8368C2",
    500: "#6442B3",
    600: "#5B3CA3",
    lavender: "#AA97D8",
    // Ink on primary[100] fills (active tabs, chips): a light lavender in dark, as 700 is a dark ink in light.
    700: "#C4B5EC",
    800: "#372462",
    900: "#2A1C4B",
  },

  secondary: {
    100: "#12343D",
    300: "#69E0F6",
    500: "#1CAEC8",
    700: "#4FC3DA",
    900: "#9BE3F0",
  },

  stroke: {
    100: "#2E2642",
    200: "#3A3152",
    strong: "#4A4066",
    300: "#4A4066",
    400: "#FB975B",
    500: "#F669E1",
    600: "#3A73DF",
    700: "#69E0F6",
  },

  alpha: {
    white8: "rgba(255, 255, 255, 0.08)",
    white18: "rgba(255, 255, 255, 0.18)",
    inverse92: "rgba(24, 16, 43, 0.92)",
  },

  accent: {
    onInk: {
      caution: "#F5C26B",
      success: "#7FD8B0",
      danger: "#F59AA0",
    },
    purpleLight: "rgba(138, 92, 230, 0.16)",
    purple: "#A98BF0",
    pinkLight: "rgba(255, 10, 128, 0.16)",
    pink: "#FF5CA8",
    peachLight: "rgba(251, 151, 91, 0.16)",
    peach: "#FB975B",
    greenLight: "rgba(27, 190, 101, 0.16)",
    green: "#3DD982",
    yellow500: "#EAB308",
    yellowLight: "rgba(234, 179, 8, 0.16)",
    amber500: "#F59E0B",
    amberLight: "rgba(245, 158, 11, 0.16)",
    tableBlue: "#7C84FF",
    tableBlueBg: "rgba(124, 132, 255, 0.16)",
    pkColor: "#FF9A5C",
    pkBg: "rgba(255, 154, 92, 0.16)",
    pkBorder: "rgba(255, 154, 92, 0.32)",
    fkColor: "#7FA6FF",
    fkBg: "rgba(127, 166, 255, 0.16)",
    fkBorder: "rgba(127, 166, 255, 0.32)",
    dataStoryPurple: "#C26BF0",
    dataStoryPurpleBg: "rgba(194, 107, 240, 0.16)",
    dataModelRed: "#FD5A49",
    dataModelRedBg: "rgba(253, 90, 73, 0.16)",
    ontologyGreen: "#3FCB9B",
    ontologyGreenBg: "rgba(63, 203, 155, 0.16)",
    metricBlue: "#00B1D1",
    metricBlueBg: "rgba(1, 189, 222, 0.16)",
    artifactBlue: "#219EFF",
    artifactBlueBg: "rgba(33, 158, 255, 0.16)",
    tribalPurple: "#A56BFF",
    warningYellow: "#EAB308",
    green500: "#4ADE80",
    greenMetricBg: "rgba(74, 222, 128, 0.16)",
    red500: "#F87171",
    turquoiseLight: "rgba(28, 174, 200, 0.16)",
    turquoise: "#1CAEC8",
    drill: {
      bg: "rgba(251, 151, 91, 0.16)",
      border: "rgba(251, 151, 91, 0.4)",
      solid: "#FB975B",
      text: "#FBB98A",
    },
    cross: {
      bg: "rgba(105, 198, 217, 0.16)",
      border: "rgba(105, 198, 217, 0.4)",
      text: "#8FE0EF",
    },
    positive: {
      bg: "rgba(31, 157, 107, 0.16)",
      text: "#5FD6A3",
      solid: "#1F9D6B",
    },
    negative: {
      bg: "rgba(210, 65, 75, 0.16)",
      text: "#F5959C",
      textStrong: "#F9B1B6",
      solid: "#D2414B",
    },
    infoBlue: {
      bg: "rgba(47, 111, 214, 0.18)",
      solid: "#6B9CF0",
    },
    danger: {
      soft: {
        bg: "rgba(210, 65, 75, 0.16)",
        border: "rgba(210, 65, 75, 0.4)",
        text: "#F5959C",
        solid: "#E5636C",
      },
    },
    caution: {
      bg: "rgba(194, 122, 11, 0.18)",
      text: "#F2C46D",
      solid: "#C27A0B",
    },
    tag: {
      success: { bg: "rgba(31, 157, 107, 0.16)", fg: "#5FD6A3" },
      danger: { bg: "rgba(210, 65, 75, 0.16)", fg: "#F5959C" },
      info: { bg: "rgba(47, 111, 214, 0.18)", fg: "#8FB4F5" },
      accent: { bg: "rgba(170, 151, 219, 0.16)", fg: "#C4B5EE" },
      neutral: { bg: "rgba(243, 241, 247, 0.08)", fg: "#C9C2D9" },
      neutralStrong: { bg: "rgba(243, 241, 247, 0.14)", fg: "#C9C2D9" },
      warning: { bg: "rgba(194, 122, 11, 0.18)", fg: "#F2C46D" },
      caution: { bg: "rgba(194, 122, 11, 0.18)", fg: "#F2C46D" },
    },
    soon: {
      bg: "rgba(251, 151, 91, 0.16)",
      text: "#FBB98A",
    },
  },

  text: {
    highEmphasis: "#F3F1F7",
    strongEmphasis: "#E2DDEC",
    mediumEmphasis: "#CFC8DF",
    lowEmphasis: "#A89FBD",
    subtleEmphasis: "#8F86A6",
    white: "#F3F1F7",
    interactive: "#AA97DB",
  },

  ai: {
    stroke:
      "linear-gradient(90deg, #FF69B4 0%, #FFA500 25%, #00CED1 75%, #1E90FF 100%)",
    chatBackground: "rgba(108, 99, 255, 0.18)",
    iconPrimary: "#AA97DB",
    iconSecondary: "#F3F1F7",
  },

  strokeGradient: {
    brand: "linear-gradient(90deg, #2665D3 0%, #B328A9 100%)",
  },

  backgroundGradient: {
    primary: "linear-gradient(135deg, #1F1A3A 0%, #2A1C3A 100%)",
    secondary: "linear-gradient(135deg, #2A1F2E 0%, #2A1C3A 100%)",
    subtle: "linear-gradient(135deg, #1A1428 0%, #1F1830 100%)",
    insight: "linear-gradient(169deg, #2A2240 4.29%, #2E1F36 96.37%)",
    chatAssistant:
      "linear-gradient(107deg, #1F2440 13.55%, #2E1F3A 47.95%, #2E2530 83.27%, #1F2440 103.12%)",
    storyCreationAi:
      "linear-gradient(108deg, #1F2440 9.73%, #2E2530 23.81%, #2E1F3A 52.44%, #1F2A44 81.42%), #1F1830",
  },

  canvas: {
    dotGrid: "#3A3152",
    tooltipDark: "#1B2D4A",
  },
  gradient: {
    lookBar: "linear-gradient(90deg, #6442B3, #22D3EE, #F472B6)",
  },
};
